import React, { useEffect, useRef, useState } from "react";
import { View, StyleSheet } from "react-native";
import { Platform } from "react-native";

interface AssessmentAudioVisualizerProps {
  deviceId?: string;
  isActive?: boolean;
  barCount?: number;
  barWidth?: number;
  barGap?: number;
  barColor?: string;
  inactiveColor?: string;
  height?: number;
}

const AssessmentAudioVisualizer: React.FC<AssessmentAudioVisualizerProps> = ({
  deviceId,
  isActive = false,
  barCount = 7,
  barWidth = 2,
  barGap = 4,
  barColor = "#667085",
  inactiveColor = "#E4E7EC",
  height = 16,
}) => {
  const [audioLevels, setAudioLevels] = useState<number[]>(new Array(barCount).fill(0));
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const microphoneRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (Platform.OS !== "web" || !isActive) {
      return;
    }

    const startAudioVisualization = async () => {
      try {
        audioContextRef.current = new (
          window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
        )();

        const audioConstraints = deviceId && deviceId !== "default" ? { deviceId: { exact: deviceId } } : true;

        const stream = await navigator.mediaDevices.getUserMedia({
          audio: audioConstraints,
        });

        microphoneRef.current = audioContextRef.current.createMediaStreamSource(stream);
        analyserRef.current = audioContextRef.current.createAnalyser();

        analyserRef.current.fftSize = 256;
        analyserRef.current.smoothingTimeConstant = 0.8;

        microphoneRef.current.connect(analyserRef.current);

        updateAudioLevels();
      } catch (error) {
        console.error("Error starting audio visualization:", error);
      }
    };

    const updateAudioLevels = () => {
      if (!analyserRef.current) return;

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyserRef.current.getByteFrequencyData(dataArray);

      const samplesPerBar = Math.floor(bufferLength / barCount);
      const newLevels = [];

      for (let i = 0; i < barCount; i++) {
        const startIndex = i * samplesPerBar;
        const endIndex = startIndex + samplesPerBar;
        let sum = 0;

        for (let j = startIndex; j < endIndex; j++) {
          sum += dataArray[j];
        }

        const average = sum / samplesPerBar;

        const normalizedLevel = Math.min(average / 128, 1);
        newLevels.push(normalizedLevel);
      }

      setAudioLevels(newLevels);
      animationFrameRef.current = requestAnimationFrame(updateAudioLevels);
    };

    startAudioVisualization();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (microphoneRef.current) {
        microphoneRef.current.disconnect();
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, [deviceId, isActive, barCount]);

  useEffect(() => {
    if (!isActive) {
      setAudioLevels(new Array(barCount).fill(0));
    }
  }, [isActive, barCount]);

  return (
    <View
      style={[styles.container, { height }]}
      accessible={false}
      accessibilityElementsHidden={true}
      importantForAccessibility="no"
      aria-hidden={true}>
      {audioLevels.map((level, index) => {
        const barHeight = isActive ? Math.max(level * height * 0.8, 2) : 2;

        return (
          <View
            key={index}
            style={[
              styles.bar,
              {
                width: barWidth,
                height: barHeight,
                backgroundColor: isActive ? barColor : inactiveColor,
                marginRight: index < barCount - 1 ? barGap : 0,
                alignSelf: "center",
              },
            ]}
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  bar: {
    borderRadius: 1,
  },
});

export default AssessmentAudioVisualizer;
